import { describeMutationError } from "@api/mutationErrors";
import { usePrincipalKey } from "@auth/sessionStore";
import { emailClient, isEmailUnavailable } from "@email/emailClient";
import { emailKeys, useEmailSetup } from "@email/emailQueries";
import {
  Button,
  FieldError,
  SettingsCard,
  Textarea,
} from "@scribenote/brian-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type EmailAssistantProfile = Awaited<
  ReturnType<typeof emailClient.getAssistant>
>;

export const EmailAssistantPreferences = () => {
  const principal = usePrincipalKey();
  const cache = useQueryClient();
  const setup = useEmailSetup(principal);
  const assistant = useQuery({
    queryKey: emailKeys.assistant(principal),
    queryFn: emailClient.getAssistant,
    enabled: !!setup.data?.mailbox && !setup.error,
  });
  const [draft, setDraft] = useState<EmailAssistantProfile | null>(null);
  const current = draft ?? assistant.data;
  const save = useMutation({
    mutationFn: emailClient.saveAssistant,
    onSuccess: async () => {
      setDraft(null);
      await cache.invalidateQueries({
        queryKey: emailKeys.principal(principal),
      });
    },
    meta: { handled: true },
  });

  if (isEmailUnavailable(setup.error)) return null;

  if (!setup.data?.mailbox) {
    return (
      <SettingsCard title="Assistant defaults">
        <p className="text-sm text-annotation">
          Connect the clinic mailbox before configuring Brian’s email defaults.
        </p>
      </SettingsCard>
    );
  }
  if (!current) {
    return assistant.isLoading ? (
      <p className="text-sm text-annotation">Loading assistant settings…</p>
    ) : (
      <FieldError>Assistant settings could not be loaded.</FieldError>
    );
  }

  return (
    <SettingsCard
      title="Assistant defaults"
      subtitle="Default instructions Brian uses when drafting email."
      data-dd-privacy="hidden"
    >
      <div className="flex flex-col gap-4">
        <Textarea
          label="Standing instructions"
          value={current.instructions}
          onChange={(event) =>
            setDraft({ ...current, instructions: event.target.value })
          }
          placeholder="Your tone, priorities, and how you want email handled"
          minRows={3}
        />
        <Button
          busy={save.isPending}
          disabled={!draft}
          onClick={() => draft && save.mutate(draft)}
        >
          Save assistant settings
        </Button>
        {save.error && (
          <FieldError>
            {describeMutationError(save.error, "save assistant settings")}
          </FieldError>
        )}
      </div>
    </SettingsCard>
  );
};
